import styled from '@emotion/styled';
import { Grid, Paper } from '@material-ui/core';
import { debounce } from 'lodash-es';
import { createElement, FC, useContext, useMemo, useState } from 'react';
import { useMount } from 'react-use';
import { useRecoilValue } from 'recoil';

import { useGetIsPC } from '../../../../../utilityHooks/useGetIsPC';
import CreationSetsFieldDefaultView from '../../../../component/compound/BasicProjectSettingEditor/CreationSetsFieldDefaultView';
import ProjectTitleEditor from '../../../../component/compound/BasicProjectSettingEditor/ProjectTitleEditor';
import SectionHeader from '../../../../component/compound/BasicProjectSettingEditor/SectionHeader';
import InputtedProjectPointTotal from '../../../../component/compound/InputtedProjectPointTotal';
import Spinner from '../../../../component/compound/Spinner';
import { SCREEN_LARGE } from '../../../../constant/pageWidth';
import { SPACING_HUGE, SPACING_REGULAR, SPACING_UNIT } from '../../../../constant/spacing';
import { context as DependencyContext } from '../../../../context/dependency';
import { context as StateContext } from '../../../../context/state';
import mq from '../../../../styleUtility/mediaquery';
import { InputtedCreativeFormat } from '../../../../types/InputtedCreativeFormat';
import { editingProjectStateAtom, editingProjectStateErrorsAtom } from '../../atoms/editingProjectStateAtom';
import { isProjectFlatAtom } from '../../atoms/isProjectFlatAtom';
import { projectAtom, projectLoadingAtom } from '../../atoms/projectAtom';
import { getOrganization, getRegion } from '../../domain/helperFunctions';
import { inputtedCreativeFormatsConverter } from '../../domain/inputtedCreativeFormats';
import { hasAnyErrorsOfMandatoryFieldsExceptForTitle } from '../../domain/stateValidator';
import { useEditingProjectState } from '../../hooks/useEditingProjectState';
import useUpdateProject from '../../hooks/useUpdateProject';
import LeftWorkflowStepper from '../LeftWorkflowStepper';
import MobileOnlyWorkflowStepper from '../MobileOnlyWorkflowStepper';
import AddOriginalFormatsDialog from './AddOriginalFormatsDialog';
import AddResizeFormatsDialog from './AddResizeFormatsDialog';
import BasicSettingsActions from './BasicSettingsActions';
import CreationSet from './CreationSet';

type Props = {
  setConfirmAlert: (state: boolean) => void;
};

// TODO: ComponentもProjectのStepに則って命名したほうが分かりやすいと思う。他のComponentで既にDueScreenのように
//  ProjectEditingStepと同じものもある
const BasicSettingScreen: FC<Props> = ({ setConfirmAlert }) => {
  const isPC = useGetIsPC();
  const isFlat = useRecoilValue(isProjectFlatAtom);
  const project = useRecoilValue(projectAtom);
  const isLoading = useRecoilValue(projectLoadingAtom);
  const state = useRecoilValue(editingProjectStateAtom);
  const errors = useRecoilValue(editingProjectStateErrorsAtom);

  const {
    setTitle,
    addInputtingCreativeFormats,
    clearAllSelectedInputtingCreativeFormats,
    removeCreativeFormat,
  } = useEditingProjectState();
  const {
    updateDraftProject,
    updateDraftProjectAndGoToDifferentTab,
    updateDraftProjectAndGoToProjectsList,
  } = useUpdateProject();

  const [isResizeFormatsDialogOpen, setIsResizeFormatsDialogOpen] = useState(false);
  const [isOriginalFormatsDialogOpen, setIsOriginalFormatsDialogOpen] = useState(false);
  const [originalCreativeFormat, setOriginalCreativeFormat] = useState<InputtedCreativeFormat | undefined>(undefined);

  const { language, lastAccessedOrganization, availableOrganizations } = useContext(StateContext);
  const { organizationUsecase } = useContext(DependencyContext);

  const region = getRegion(lastAccessedOrganization);
  const organization = getOrganization(lastAccessedOrganization, availableOrganizations);

  // initialize variables
  const inputtedInputtingCreativeFormatsFilter = useMemo(() => state?.inputtingCreativeFormatsFilter ?? null, [
    state?.inputtingCreativeFormatsFilter,
  ]);

  const availableMatchingFormats = project?.projectType.matchingFormatGroup?.availableFormats;

  const selectedCreativeTypeIndex = useMemo(
    () =>
      inputtedInputtingCreativeFormatsFilter
        ? inputtedInputtingCreativeFormatsFilter.creativeTypes
            .map((creativeType, index) => (creativeType.selected ? index.toString() : null))
            .filter((item) => item !== null)
        : null,
    [inputtedInputtingCreativeFormatsFilter],
  );

  const selectedPlacementIndex = useMemo(
    () =>
      inputtedInputtingCreativeFormatsFilter
        ? inputtedInputtingCreativeFormatsFilter.networksAndPlacements
            .map((networksAndPlacement, index) => (networksAndPlacement.selected ? index.toString() : null))
            .filter((item) => item !== null)
        : null,
    [inputtedInputtingCreativeFormatsFilter],
  );

  const filteredList = useMemo(() => inputtedCreativeFormatsConverter(state).filter(), [state]);

  useMount(async () => {
    await organizationUsecase.refreshPointBalance(organization.id);
  });

  // Flat表示時、最初のCreativeFormatを選択した際、納品期日データをサーバから取得するために

  if (isLoading || !state || !state.inputtingCreativeFormats.length) {
    return <Spinner fullHeight />;
  }

  const onlyCompetition = availableMatchingFormats?.length === 1 && availableMatchingFormats[0] === 'competition';

  return (
    <Root isFlat>
      <Grid container direction="row" justifyContent="flex-start" alignItems="stretch" spacing={isPC ? 4 : 2}>
        <LeftWorkflowStepper
          step="format"
          updateDraftAndGoToTargetStep={(targetStep) =>
            updateDraftProjectAndGoToDifferentTab({ state, currentStep: 'format', targetStep })
          }
        />
        <Grid item xs={12} sm={12} md={12} lg={9}>
          {/* Inputted Project Total Point */}
          <InputtedProjectPointTotal step="format" />

          {/* 発注するプロジェクトのタイトルの編集Text field */}
          <SectionHeader
            titleMessageId={'project_title_editor.title'}
            supportingTextMessageId={'project_title_editor.description'}
            region={region}
          />
          <ProjectTitleEditorContainer>
            <ProjectTitleEditor
              title={state.title}
              projectType={project?.projectType}
              language={language}
              onChangeTitle={debounce((title: string) => {
                setTitle(title);
              }, 250)}
              errors={errors}
            />
          </ProjectTitleEditorContainer>

          {/* CreationSetsを追加する（発注するプロジェクトのフォーマットとリサイズの設定なども） */}
          <SectionHeader
            titleMessageId={'project_editing_page.basic_setting.creative_formats.heading'}
            supportingTextMessageId={'project_editing_page.basic_setting.creative_formats.description'}
            region={region}
          />
          {state.creationSet && (
            <CreationSet
              key={`CreativeFormat-selector`}
              allCreativeFormats={state.creationSet.creativeFormats}
              onlyCompetition={onlyCompetition}
              onClickAddOriginalFormatsButton={() => {
                clearAllSelectedInputtingCreativeFormats();
                setIsOriginalFormatsDialogOpen(true);
                setIsResizeFormatsDialogOpen(false);
              }}
              onClickAddResizeFormatsButton={(_originalCreativeFormat: InputtedCreativeFormat) => {
                clearAllSelectedInputtingCreativeFormats(_originalCreativeFormat);
                setIsOriginalFormatsDialogOpen(false);
                setIsResizeFormatsDialogOpen(true);
                setOriginalCreativeFormat(_originalCreativeFormat);
              }}
              onClickDeleteCreativeFormatButton={(props) => {
                removeCreativeFormat({
                  ...props,
                  resetSelectedCreativeFormatsState: () => {
                    setOriginalCreativeFormat(undefined);
                  },
                });
              }}
              region={region}
              creationSet={state.creationSet}
              project={project}
            />
          )}
          {state.creationSet === null && (
            <CreationSetsFieldDefaultView
              setIsOriginalFormatsDialogOpen={setIsOriginalFormatsDialogOpen}
              setIsResizeFormatsDialogOpen={setIsResizeFormatsDialogOpen}
            />
          )}
        </Grid>
      </Grid>

      {originalCreativeFormat && (
        <AddResizeFormatsDialog
          creativeFormats={filteredList}
          inputtingCreativeFormatsFilter={inputtedInputtingCreativeFormatsFilter}
          originalCreativeFormat={originalCreativeFormat}
          selectedPlacementIndex={selectedPlacementIndex}
          isOpen={isResizeFormatsDialogOpen}
          onClose={() => {
            setIsResizeFormatsDialogOpen(false);
            clearAllSelectedInputtingCreativeFormats(originalCreativeFormat);
          }}
          onSubmit={() => {
            setIsResizeFormatsDialogOpen(false);
            addInputtingCreativeFormats(originalCreativeFormat);
          }}
        />
      )}

      <AddOriginalFormatsDialog
        creativeFormats={filteredList}
        inputtingCreativeFormatsFilter={inputtedInputtingCreativeFormatsFilter}
        selectedCreativeTypeIndex={selectedCreativeTypeIndex}
        selectedPlacementIndex={selectedPlacementIndex}
        isOpen={isOriginalFormatsDialogOpen}
        onClose={() => {
          setIsOriginalFormatsDialogOpen(false);
          clearAllSelectedInputtingCreativeFormats(originalCreativeFormat);
        }}
        onSubmit={() => {
          setIsOriginalFormatsDialogOpen(false);
          addInputtingCreativeFormats();
          if (isFlat && state.creationSet === null) {
            updateDraftProject();
          }
        }}
      />

      {!isFlat && (
        <MobileOnlyWorkflowStepper
          step="format"
          updateDraftAndGoToTargetStep={(targetStep) =>
            updateDraftProjectAndGoToDifferentTab({ state, currentStep: 'format', targetStep })
          }
        />
      )}
      {!isFlat && (
        <BasicSettingsActions
          isDisabled={hasAnyErrorsOfMandatoryFieldsExceptForTitle(errors)}
          saveAndGoForward={() => {
            setConfirmAlert(false);
            updateDraftProjectAndGoToDifferentTab({
              state,
              currentStep: 'format',
              targetStep: 'due',
            });
          }}
          saveAndGoToProjectList={() => {
            setConfirmAlert(false);
            updateDraftProjectAndGoToProjectsList({ state, currentStep: 'format' });
          }}
        />
      )}
    </Root>
  );
};

const Root = styled.section<{ isFlat: boolean }>`
  width: auto;
  max-width: ${SCREEN_LARGE}px;
  margin: ${SPACING_HUGE}px auto ${({ isFlat }) => (isFlat ? 0 : SPACING_UNIT * 12)}px;
  padding-bottom: ${({ isFlat }) => (isFlat ? SPACING_UNIT * 4 : SPACING_UNIT * 12)}px;
  ${mq('sp')} {
    margin-top: ${SPACING_REGULAR}px;
    padding-left: ${SPACING_REGULAR}px;
    padding-right: ${SPACING_REGULAR}px;
    padding-bottom: ${SPACING_UNIT * 6}px;
  }
`;

const ProjectTitleEditorContainer = styled(Paper)`
  margin-bottom: ${SPACING_HUGE}px;
  padding: ${SPACING_REGULAR}px;
`;

export default BasicSettingScreen;
